import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";

interface NumberTextInputProps {
  id?: string;
  value: unknown;
  isInteger?: boolean;
  minimum?: number;
  maximum?: number;
  onChange: (val: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * A text-based numeric input that supports decimal entry without browser
 * spinners or scroll-to-change behavior. Uses a local string buffer so
 * intermediate states like "0." or "12.00" are preserved while typing.
 * Parses and commits the final value on blur, with optional min/max clamping.
 */
export function NumberTextInput({
  id,
  value,
  isInteger = false,
  minimum,
  maximum,
  onChange,
  disabled,
  placeholder,
  className,
}: NumberTextInputProps) {
  const [localValue, setLocalValue] = useState(() =>
    typeof value === "number" ? String(value) : "",
  );

  useEffect(() => {
    const external = typeof value === "number" ? String(value) : "";
    setLocalValue((prev) => {
      if (prev === external) {
        return prev;
      }
      const parsed = isInteger ? parseInt(prev, 10) : parseFloat(prev);
      if (!isNaN(parsed) && parsed === value) {
        return prev;
      }
      return external;
    });
  }, [value, isInteger]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      if (raw === "" || raw === "-") {
        setLocalValue(raw);
        if (raw === "") {
          onChange(null);
        }
        return;
      }

      const pattern = isInteger ? /^-?\d*$/ : /^-?\d*\.?\d*$/;
      if (!pattern.test(raw)) {
        return;
      }

      setLocalValue(raw);

      const parsed = isInteger ? parseInt(raw, 10) : parseFloat(raw);
      if (!isNaN(parsed)) {
        onChange(parsed);
      }
    },
    [isInteger, onChange],
  );

  const handleBlur = useCallback(() => {
    if (localValue === "" || localValue === "-") {
      return;
    }

    const parsed = isInteger
      ? parseInt(localValue, 10)
      : parseFloat(localValue);
    if (isNaN(parsed)) {
      return;
    }

    const clamped =
      minimum !== undefined && parsed < minimum
        ? minimum
        : maximum !== undefined && parsed > maximum
          ? maximum
          : parsed;

    if (clamped !== parsed) {
      onChange(clamped);
    }

    setLocalValue(String(clamped));
  }, [localValue, isInteger, minimum, maximum, onChange]);

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
    />
  );
}
